/**
 * Generation progress screen — polls `GET /api/ai-jobs/:id` every 3s. This
 * is the mechanism, not a fallback.
 *
 * An `EventSource` branch used to sit in front of the polling below, tried
 * first with polling as its `onerror` recovery path. It was removed
 * (2026-09-20) after checking the real endpoint against prod: `GET
 * /api/ai-jobs/<id>/sse?token=<jwt>` answers 401 "Not authenticated", every
 * time. The backend's auth dependency reads the `Authorization` header and
 * decodes it as a JWT; it never looks at `?token=`. And it can't, really —
 * `EventSource` has no way to attach a header, so the token has to travel
 * in the URL if it travels at all, which is also just a bad idea on its
 * own (it'd end up in server/proxy access logs). So the connection failed
 * immediately, `onerror` fired, and polling — silently — has been the only
 * thing actually running in production the whole time. A second, unrelated
 * bug was waiting behind that one: the backend emits *named* SSE events
 * (`event: progress`), which a plain `EventSource.onmessage` never
 * receives, by spec. Fixing only the auth side would have swapped "works
 * by accident via the fallback" for "hangs, silently, for two minutes" —
 * worse than the bug it replaced. Don't re-add SSE here without fixing
 * both of those server-side first, and wiring `onmessage` (or a named
 * `addEventListener("progress", …)`) to match whatever it actually sends.
 *
 * Backend contract (revised 2026-09-20): `progress_label` is one of "guard" |
 * "writing" | "review" | "cover" | "voice" — note the step order, cover now
 * comes before voice, since the cover is generated *before* the audio
 * specifically so this screen can show real cover art during the remaining
 * wait instead of a placeholder. `progress_total` is always 100, and
 * `progress_done` is an integer 0..100 weighted by step and monotonic
 * (start-of-step milestones: guard 0, writing 3, review 28, cover 40,
 * voice 48, done 100 — only "voice" is interpolated continuously in
 * between; "cover", like every other step, sits flat). The mapping onto the
 * 4 displayed step rows and the fraction for the progress bar lives in
 * `lib/jobProgress.ts` (pure, unit-tested), including a legacy fallback for
 * a not-yet-redeployed backend that still sends French prose + a 0..5
 * counter.
 *
 * This component owns four UX guardrails on top of that pure mapping:
 *   - monotonic display — the bar/steps never go backwards, even if a
 *     stale poll reply reports less progress than already shown;
 *   - "creep" — between real events the bar eases toward (but never onto)
 *     the next milestone, so long silences (e.g. "writing" holding at 3%
 *     for ~35s with zero intermediate events) don't read as frozen. Off
 *     entirely under `prefers-reduced-motion`;
 *   - sticky title — once a poll reply carries `result.title` (published
 *     mid-run, before the "cover" step starts), it stays on screen even if
 *     a later reply comes back without one (see `lib/jobProgress.ts`'s
 *     `stickyTitle`);
 *   - sticky cover — once `result.cover_ready` is seen, the story is
 *     fetched exactly once for its `cover_svg` (never on every 3s poll
 *     tick — see `lib/jobProgress.ts`'s `shouldFetchCover`), and the real
 *     cover replaces the striped placeholder and never goes back, even if
 *     a later poll reply omits `cover_ready` (best-effort generation: that
 *     never means "undo the cover already shown").
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { Container, PageLayout, Headline, MutedText, Button, IconButton, Eyebrow, ProgressBar } from "../components/ui";
import { Cover } from "../components/Cover";
import { getAiJob, getStory } from "../lib/dataSource";
import { useChildren } from "../lib/childrenStore";
import { universeByKey } from "../lib/universes";
import { computeJobProgress, creepStep, nextMilestoneAbove, shouldFetchCover, stickyTitle } from "../lib/jobProgress";
import { placeholderStyle } from "../lib/colorways";
import type { AiJob, FailReason } from "../lib/types";

const STEP_KEYS = ["generation.stepWriting", "generation.stepReview", "generation.stepCover", "generation.stepVoice"];

const CREEP_TICK_MS = 400;
// Fraction of the remaining gap to the ceiling closed per tick. This isn't
// tuned by feel: the resulting time constant (~40s to close 63% of any
// gap, at 400ms/tick — 1-(1-0.01)^100 ≈ 0.63) is calibrated against the
// pipeline's longest silence, "writing" holding flat at 3% for ~35s with
// zero intermediate events — that's the freeze the creep exists to paper
// over. "voice" is the opposite case: real chunks land every ~5-7s with
// +4-5 points each, and a 6s gap should only close ~14% of the remaining
// gap (57%→~62%), tracking the real rate instead of racing ahead of it and
// then sitting stalled at the ceiling for the rest of a ~70s step — a
// faster rate (0.06 closes ~60% of the gap in 6s) recreated exactly the
// "frozen" look it was meant to avoid, just ten seconds later. If the
// backend's step weights are ever recalibrated, re-derive this rate from
// whichever step then has the longest gap between events, not from taste.
const CREEP_RATE = 0.01;
const CREEP_EPSILON = 0.01; // stay this far short of the next milestone, never touch it

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/**
 * Turns the raw, possibly out-of-order `{ fraction, stepIndex }` from
 * `computeJobProgress` into what the screen actually paints: a monotonic,
 * gently-creeping display fraction, and a step index that only ever moves
 * forward (so the checklist above never un-checks a row).
 */
function useDisplayedProgress(job: AiJob | null, reducedMotion: boolean) {
  const { fraction, stepIndex } = computeJobProgress(job);
  const isDone = job?.status === "done";

  const maxFractionRef = useRef(0);
  const maxStepRef = useRef(-1);
  const [displayFraction, setDisplayFraction] = useState(0);
  const [committedStep, setCommittedStep] = useState(-1);

  useEffect(() => {
    maxFractionRef.current = Math.max(maxFractionRef.current, fraction);
    if (stepIndex > maxStepRef.current) {
      maxStepRef.current = stepIndex;
      setCommittedStep(stepIndex);
    }
    if (isDone) {
      setDisplayFraction(1);
    } else if (reducedMotion) {
      // No creep, no transition: jump straight to the real value.
      setDisplayFraction(maxFractionRef.current);
    } else {
      setDisplayFraction((d) => Math.max(d, maxFractionRef.current));
    }
  }, [fraction, stepIndex, isDone, reducedMotion]);

  useEffect(() => {
    if (isDone || reducedMotion) return;
    const id = setInterval(() => {
      setDisplayFraction((d) => {
        const ceiling = Math.max(0, nextMilestoneAbove(maxFractionRef.current) - CREEP_EPSILON);
        return creepStep(d, ceiling, CREEP_RATE);
      });
    }, CREEP_TICK_MS);
    return () => clearInterval(id);
  }, [isDone, reducedMotion]);

  return { displayFraction, stepIndex: committedStep };
}

/**
 * Sticky story title (see `lib/jobProgress.ts`'s `stickyTitle`): once
 * `job.result.title` arrives, it's kept forever, even if a later poll
 * reply reports the job without one.
 */
function useStickyTitle(job: AiJob | null): string | undefined {
  const [title, setTitle] = useState<string | undefined>(undefined);
  useEffect(() => {
    setTitle((current) => stickyTitle(current, job?.result?.title));
  }, [job]);
  return title;
}

/**
 * Sticky cover image (see `lib/jobProgress.ts`'s `shouldFetchCover`): once a
 * poll reply carries `result.cover_ready`, fetches `GET
 * /chouette/stories/{id}` exactly once for its `cover_svg` — the image
 * never rides the poll itself, `cover_ready` is only ever a one-shot
 * trigger. Once a cover is held, it's kept forever, even if a later reply
 * omits `cover_ready` (the pipeline is best-effort: absent doesn't mean
 * "take it back", just "nothing new to fetch").
 */
function useCoverImage(job: AiJob | null, fallbackStoryId: string | null): string | null {
  const [coverSvg, setCoverSvg] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!shouldFetchCover(job, { alreadyFetched: fetchedRef.current, alreadyHaveCover: coverSvg !== null })) return;
    const targetId = job?.result?.story_id ?? fallbackStoryId;
    if (!targetId) return;

    fetchedRef.current = true;
    let cancelled = false;
    void getStory(targetId)
      .then((story) => {
        if (!cancelled && story.cover_svg) setCoverSvg(story.cover_svg);
      })
      .catch(() => {
        // Transient network hiccup: allow a later poll tick (still
        // reporting cover_ready) to retry, rather than giving up forever.
        if (!cancelled) fetchedRef.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [job, fallbackStoryId, coverSvg]);

  return coverSvg;
}

export default function Generation() {
  const { t } = useTranslation();
  const { jobId } = useParams<{ jobId: string }>();
  const [params] = useSearchParams();
  const storyId = params.get("story");
  const universe = universeByKey(params.get("universe"));
  const navigate = useNavigate();
  const { currentChild } = useChildren();
  const [job, setJob] = useState<AiJob | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;

    const applyJob = (j: AiJob) => {
      if (cancelled) return;
      setJob(j);
      if (j.status === "done" || j.status === "failed" || j.status === "cancelled") {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    };

    void getAiJob(jobId).then(applyJob);
    pollRef.current = setInterval(() => {
      void getAiJob(jobId).then(applyJob);
    }, 3000);

    return () => {
      cancelled = true;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobId]);

  useEffect(() => {
    if (job?.status === "done") {
      const target = job.result?.story_id ?? storyId;
      if (target) navigate(`/story/${target}`, { replace: true });
    }
  }, [job, storyId, navigate]);

  const reducedMotion = usePrefersReducedMotion();
  const { displayFraction, stepIndex: currentStepIndex } = useDisplayedProgress(job, reducedMotion);
  const storyTitle = useStickyTitle(job);
  const coverSvg = useCoverImage(job, storyId);

  const failReason: FailReason | undefined = job?.status === "failed" ? job.result?.fail_reason ?? "other" : undefined;

  if (failReason) {
    return (
      <PageLayout>
        <Container className="pt-16 flex flex-col items-center text-center gap-4">
          <Headline>{t("generation.failedTitle")}</Headline>
          <MutedText>
            {failReason === "brief_refused" ? t("generation.failedBriefRefused") : t("generation.failedOther")}
          </MutedText>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate("/create")}>{t("generation.retry")}</Button>
            <Button onClick={() => navigate("/")}>{t("generation.backHome")}</Button>
          </div>
        </Container>
      </PageLayout>
    );
  }

  const UniverseIcon = universe?.icon;
  const announcedStepKey = currentStepIndex >= 0 ? STEP_KEYS[Math.min(currentStepIndex, STEP_KEYS.length - 1)] : null;

  return (
    <PageLayout>
      {/* The screen used to be a dead end: no bottom nav, no control, and a
          generation that runs for two minutes — on the installed PWA there
          is not even a browser back button (reported 2026-09-19). */}
      <Container className="pt-4">
        <IconButton onClick={() => navigate("/")} aria-label={t("common.back")}>
          <ArrowLeft size={18} />
        </IconButton>
      </Container>
      <Container className="pt-4 flex flex-col items-center text-center gap-6">
        {/* Relative frame holding both layers at the same fixed footprint,
            so the cover's arrival — anywhere from a few seconds to over a
            minute in — never shifts anything around it. The placeholder
            stays mounted (just made invisible) rather than being removed,
            and the cover fades in on mount via a one-shot CSS animation
            (`motion-safe:` — instant under prefers-reduced-motion) instead
            of a transition, since it isn't in the DOM yet to transition
            from. Once shown, it's sticky (see useCoverImage) and never
            reverts to the placeholder. */}
        <div className="relative w-40 h-40 rounded-card shadow-solid">
          {/* No card to read `cover_colorway` from on this screen — the
              story is still being written — so this is the one placeholder
              that always hashes locally. Keyed on the story rather than the
              job so the disc keeps its colour if the screen is reopened on a
              retried job. */}
          <div
            className={`absolute inset-0 rounded-card cover-placeholder motion-safe:animate-breathe flex items-center justify-center ${
              coverSvg ? "invisible" : ""
            }`}
            style={placeholderStyle(null, storyId ?? jobId ?? "")}
          >
            {UniverseIcon && (
              // On a solid disc: the stripes are one accent over its own dark
              // variant, and a line icon drawn straight on them disappears.
              <span className="flex items-center justify-center w-24 h-24 rounded-full bg-night shadow-solid">
                <UniverseIcon size={44} className="text-paper" strokeWidth={1.5} />
              </span>
            )}
          </div>
          {coverSvg && (
            <Cover
              svg={coverSvg}
              title={storyTitle ?? t("generation.coverAlt")}
              rounded="rounded-card"
              className="absolute inset-0 motion-safe:animate-fade-in"
            />
          )}
        </div>
        <div>
          {universe && <Eyebrow>{t(universe.labelKey)}</Eyebrow>}
          <Headline>{t("generation.headline", { name: currentChild?.first_name ?? "" })}</Headline>
          {/* Reserved from the first render (not mounted only once known) so
              the title's arrival — anywhere from a few seconds to over a
              minute in — never shifts anything below it on a screen that's
              watched fixedly for two minutes. Two lines' worth of height,
              since a long story title can wrap; fades in, respecting
              prefers-reduced-motion via the same motion-safe: convention as
              ProgressBar; never disappears once set (see useStickyTitle). */}
          <div className="min-h-[3.5rem] mt-1 flex items-center justify-center px-2">
            <p
              className={`font-heading font-semibold text-lg leading-snug text-sun line-clamp-2 motion-safe:transition-opacity motion-safe:duration-700 motion-safe:ease-out ${
                storyTitle ? "opacity-100" : "opacity-0"
              }`}
            >
              {storyTitle ?? ""}
            </p>
          </div>
          <p aria-live="polite" className="sr-only">
            {storyTitle ? t("generation.titleAnnounce", { title: storyTitle }) : ""}
          </p>
          <MutedText className="mt-2">{t("generation.estimate")}</MutedText>
        </div>
        <div className="w-full max-w-xs">
          <ProgressBar value={displayFraction} label={t("generation.progressLabel")} />
          {/* Announces the current step once per actual change, not on every
              creep tick — the text only changes when currentStepIndex does. */}
          <p aria-live="polite" className="sr-only">
            {announcedStepKey ? t("generation.currentStepAnnounce", { step: t(announcedStepKey) }) : ""}
          </p>
        </div>
        <ul className="w-full max-w-xs flex flex-col gap-2 text-left">
          {STEP_KEYS.map((key, i) => {
            const done = currentStepIndex > i || job?.status === "done";
            const active = currentStepIndex === i && job?.status !== "done";
            return (
              <li key={key} className="flex items-center gap-3">
                {done ? (
                  <Check size={18} className="text-mint shrink-0" />
                ) : active ? (
                  <Loader2 size={18} className="text-sun shrink-0 animate-spin" />
                ) : (
                  <span className="w-[18px] h-[18px] rounded-full border border-line shrink-0" />
                )}
                <span
                  className={`font-heading text-sm font-semibold ${
                    done ? "text-mint" : active ? "text-paper" : "text-violet-muted"
                  }`}
                >
                  {t(key)}
                </span>
              </li>
            );
          })}
        </ul>
        <Button variant="secondary" className="w-full max-w-xs" onClick={() => navigate("/")}>
          {t("generation.leave")}
        </Button>
      </Container>
    </PageLayout>
  );
}
